import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SecurityGuard from "../../guards/SecurityGuard";
import { useGuardRequest } from "../../context/useGuardRequest";
import ProcessingDialog from "../../library/ProcessingDialog";
import Dialog from "../../library/Dialog";
import {
  apiMessage,
  normalizeResponseData,
  ConfirmWriteDialog,
} from "./partnerShared";
import { listCampaigns } from "../../../contexts/services/admin/promoCodeServices";
import PartnersDialog from "./actions/PartnersDialog";
import PromoCodesTab from "./PromoCodesTab";

const PromoCodeConsole = () => {
  const [partnersOpen, setPartnersOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState("");
  const [issuedKey, setIssuedKey] = useState(null);
  const [review, setReview] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snack, setSnack] = useState(null);

  const { guardProps, runActionWithMiddleware } = useGuardRequest({
    policy: { managePromoCodes: true },
  });
  const busy = updating || guardProps.open;

  const refreshCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError("");
    try {
      const response = await listCampaigns();
      const data = normalizeResponseData(response);
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
    } catch (error) {
      setCampaignsError(apiMessage(error, "Could not load partners."));
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshCampaigns();
  }, [refreshCampaigns]);

  const confirmReview = async (reason) => {
    const action = review;
    setReview(null);
    if (!action?.run) return;
    await runActionWithMiddleware({
      actionKey: "managePromoCodes",
      details: action.summary,
      execute: async () => {
        setUpdating(true);
        try {
          await action.run(reason);
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, lg: 3 },
        minWidth: 0,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        "& .MuiButton-root": {
          textTransform: "none",
          borderRadius: 1.5,
          fontWeight: 600,
        },
      }}
    >
      <Stack spacing={2.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent='space-between'
          gap={2}
          sx={{ flexShrink: 0 }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Avatar
              variant='rounded'
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <ConfirmationNumberOutlinedIcon />
            </Avatar>
            <Box>
              <Typography component='h1' variant='h5' fontWeight={750}>
                Promo codes
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Manage promo codes, discounts, and redemptions.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant='outlined'
            startIcon={<HandshakeOutlinedIcon />}
            disabled={busy}
            aria-haspopup='dialog'
            aria-expanded={partnersOpen}
            onClick={() => setPartnersOpen(true)}
            sx={{ alignSelf: { xs: "flex-start", sm: "center" }, whiteSpace: "nowrap" }}
          >
            Manage partners
          </Button>
        </Stack>

        <PromoCodesTab
          active={!partnersOpen}
          campaigns={campaigns}
          campaignsLoading={campaignsLoading}
          campaignsError={campaignsError}
          busy={busy}
          onReview={setReview}
          onSnack={setSnack}
        />
      </Stack>

      <PartnersDialog
        open={partnersOpen}
        onClose={() => setPartnersOpen(false)}
        campaigns={campaigns}
        loading={campaignsLoading}
        error={campaignsError}
        busy={busy}
        reviewing={Boolean(review)}
        issuedKey={issuedKey}
        onIssuedKey={setIssuedKey}
        onDismissKey={() => setIssuedKey(null)}
        onRefresh={refreshCampaigns}
        onReview={setReview}
        onSnack={setSnack}
      />

      <ConfirmWriteDialog
        review={review}
        busy={busy}
        onClose={() => setReview(null)}
        onConfirm={confirmReview}
      />
      <ProcessingDialog loading={updating} />
      <SecurityGuard {...guardProps} />
      <Dialog
        open={Boolean(snack)}
        onClose={() => setSnack(null)}
        title={snack?.severity === "error" ? "Could not complete this action" : "Saved"}
        content={snack?.message || ""}
        confirmText='OK'
        cancelText='Close'
        onConfirm={() => setSnack(null)}
        maxWidth='xs'
        fullScreen={false}
        paperSx={{ borderRadius: 3, border: 1, borderColor: "divider" }}
        headerSx={{ p: 2.5 }}
        contentSx={{ p: 2.5, overflowWrap: "anywhere" }}
        actionsSx={{ px: 2.5, py: 1.5 }}
        confirmButtonSx={{ boxShadow: "none", borderRadius: 1.5, textTransform: "none" }}
      />
    </Box>
  );
};

export default PromoCodeConsole;
